/**
 * leadReopenApi — Task 5 (2026-09-25 Lost/reopen/points fix round, D4).
 *
 * `POST /leads/:id/reopen` — backend `lead-reopen.routes.ts` (Task 1). Brings
 * a Lost lead back through the stage engine, revoking the Lost −100 milestone.
 * Coordinators must carry `onBehalfOfUserId` (same scope rule as every other
 * dealer/prospect route — `resolveDealerAccess` reads it from the POST body).
 *
 * 🔒 Parses the real captured replies
 * (`__fixtures__/lead-reopen.wire-capture.json`, captured by the backend
 * route's own test) through zod, so a field the server drops or renames
 * breaks this file's own test instead of silently blanking the drawer.
 */
import { z } from 'zod';
import { api } from '@/utils/api';

export interface LeadReopenResult {
  success: true;
  fromStage: string;
  toStage: string;
  progressScore: number | null;
}

const leadReopenResultSchema = z.object({
  success: z.literal(true),
  fromStage: z.string().min(1),
  toStage: z.string().min(1),
  progressScore: z.number().nullish().transform((v) => v ?? null),
});

export function parseLeadReopenResult(raw: unknown): LeadReopenResult {
  const result = leadReopenResultSchema.safeParse(raw);
  if (!result.success) throw new Error('Invalid lead reopen response');
  return result.data;
}

export interface LeadReopenOptions {
  /** 🔒 Sent ONLY for a coordinator (dealerActsOnBehalf) — never widens scope. */
  onBehalfOfUserId?: string | null;
}

export async function reopenLead(
  leadId: string,
  options: LeadReopenOptions = {},
): Promise<LeadReopenResult> {
  const res = await api.post(`/leads/${leadId}/reopen`, {
    ...(options.onBehalfOfUserId ? { onBehalfOfUserId: options.onBehalfOfUserId } : {}),
  });
  return parseLeadReopenResult(res.data);
}

/**
 * The server's own refusal, read off the wire:
 *  - 403 `{ error }` (`canWorkProspect`'s scope refusal) — printed VERBATIM,
 *    never rewritten. Falls back to the one sentence the route always sends
 *    ("You cannot reopen this lead.") if `error` is somehow missing.
 *  - 409 `{ reason: 'not_lost' }` — the lead moved on before the click
 *    landed. The wire carries no sentence for this one (see the capture),
 *    so this is the one line this file supplies instead of leaving the
 *    screen with nothing to show.
 * Null for anything else (network failure, 5xx) — the caller keeps its own
 * generic wording for those.
 */
export function reopenLeadErrorMessage(err: unknown): string | null {
  const res = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (!res) return null;
  const data = res.data as { error?: unknown; reason?: unknown } | undefined;
  if (res.status === 403) {
    const msg = typeof data?.error === 'string' && data.error.trim() ? data.error.trim() : null;
    return msg ?? 'You cannot reopen this lead.';
  }
  if (res.status === 409) {
    if (data?.reason === 'not_lost') return 'This lead is no longer Lost — nothing to reopen.';
    const msg = typeof data?.error === 'string' && data.error.trim() ? data.error.trim() : null;
    return msg;
  }
  return null;
}
